import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import PaymentMethodsList from './PaymentMethodsList';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BANK, CARD } from '../../constants/common';
import CardPayment from './CardPayment';
import BankAccountPayment from './BankAccountPayment';
import { StyledContainer } from './AddNewPaymentMethodStyles';
import {
  IframeMessage,
  receiveMessageFromIframe,
  sendMessageToParent,
} from '../../utils/iframeUtils';
import { loadEncryptionJS, loadKeyJS } from '../../scripts/PaymentScripts';
import ErrorPage from '../../components/ErrorPage/ErrorPage';
import { ComponentLoader } from '../../components/Loaders/Loader';
import { INewPaymentBankDetailsPayload, INewPaymentCardDetailsPayload } from '../../types';
import { appConstants } from '../../constants/apiConstants';

const AddNewPaymentMethod = () => {
  //Parent Screen Size
  const [isMobileView, setIsMobileView] = useState(false);

  //Encryption scripts
  const [keyJsLoadedStatus, setKeyJsLoadedStatus] = useState(false);
  const [encryptJsLoadedStatus, setEncryptJsLoadStatus] = useState(false);
  const [keyJsLoadError, setKeyJsLoadError] = useState(false);
  const [encryptJsLoadError, setEncryptJsLoadError] = useState(false);

  const isLoading = !keyJsLoadedStatus || !encryptJsLoadedStatus;
  const isError = keyJsLoadError || encryptJsLoadError;

  const [paymentMethod, setPaymentMethod] = React.useState<string>('');

  const [addPaymentError, setAddPaymentError] = React.useState('');
  const [addPaymentLoading, setAddPaymentLoading] = React.useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  // selecting payment method
  const handleSelect = (type: string) => {
    setPaymentMethod(type);
  };
  // clearing payment method to allow user to select different payment method
  const handleChangePaymentType = () => {
    setPaymentMethod('');
  };

  useEffect(() => {
    loadKeyJS((status) => {
      if (status) {
        setKeyJsLoadedStatus(true);
      } else {
        setKeyJsLoadError(true);
      }
    });
    loadEncryptionJS((status) => {
      if (status) {
        setEncryptJsLoadStatus(true);
      } else {
        setEncryptJsLoadError(true);
      }
    });
  }, []);

  const handleClose = () => {
    setPaymentMethod('');
    const message: IframeMessage = {
      type: 'CLOSE',
      payload: '',
    };
    sendMessageToParent(message, '*');
  };

  const handleMessage = (message: IframeMessage) => {
    if (message.type === 'IS_MOBILE_VIEW') {
      setIsMobileView(message.payload);
    }
    if (message.type === 'ADD_PAYMENT_METHOD_STATUS') {
      const { addPaymentMethodStatus = '', addPaymentMethodError = '' } = message.payload;
      setAddPaymentError(addPaymentMethodError);
      setAddPaymentLoading(addPaymentMethodStatus === 'loading');
      if (addPaymentMethodStatus === 'succeeded') {
        handleClose();
      }
    }
  };

  const emitUpdatedHeight = () => {
    const message: IframeMessage = {
      type: 'HEIGHT',
      payload: (containerRef?.current?.clientHeight || 0) + 10,
    };
    sendMessageToParent(message, '*');
  };

  const handleAddCardPaymentMethod = (payload: INewPaymentCardDetailsPayload) => {
    const message: IframeMessage = {
      type: 'ADD_PAYMENT_METHOD_CARD',
      payload: payload,
    };
    sendMessageToParent(message, '*');
  };
  const handleAddBankAccountPaymentMethod = (payload: INewPaymentBankDetailsPayload) => {
    const message: IframeMessage = {
      type: 'ADD_PAYMENT_METHOD_BANK_ACCOUNT',
      payload: payload,
    };
    sendMessageToParent(message, appConstants.parentURL);
  };

  useEffect(() => {
    const unsubscribe = receiveMessageFromIframe(appConstants.parentURL, handleMessage);
    return () => {
      unsubscribe();
    };
  }, []);

  if (isError) {
    return (
      <ErrorPage
        title='Something went wrong'
        message='Failed to load encryption scripts'
      ></ErrorPage>
    );
  }

  return (
    <StyledContainer ref={containerRef}>
      {isLoading ? (
        <ComponentLoader />
      ) : (
        <>
          {!paymentMethod && (
            <>
              <Button sx={{ justifyContent: 'flex-start', p: 4 }} onClick={handleClose}>
                <ArrowBackIcon sx={{ mr: 4 }} />
                Back to Payment Methods
              </Button>
              <PaymentMethodsList emitHeight={emitUpdatedHeight} onSelect={handleSelect} />
            </>
          )}
          {paymentMethod === CARD && (
            <div>
              <Button sx={{ justifyContent: 'flex-start', p: 4 }} onClick={handleChangePaymentType}>
                <ArrowBackIcon sx={{ mr: 4 }} />
                Back to Method Type
              </Button>
              <CardPayment
                emitHeight={emitUpdatedHeight}
                isMobileView={isMobileView}
                handleAddPaymentMethod={handleAddCardPaymentMethod}
                addPaymentLoading={addPaymentLoading}
                addPaymentError={addPaymentError}
              />
            </div>
          )}
          {paymentMethod === BANK && (
            <div>
              <Button sx={{ justifyContent: 'flex-start', p: 4 }} onClick={handleChangePaymentType}>
                <ArrowBackIcon sx={{ mr: 4 }} />
                Back to Method Type
              </Button>
              <BankAccountPayment
                isMobileView={isMobileView}
                emitHeight={emitUpdatedHeight}
                handleAddPaymentMethod={handleAddBankAccountPaymentMethod}
                addPaymentLoading={addPaymentLoading}
                addPaymentError={addPaymentError}
              />
            </div>
          )}
        </>
      )}
    </StyledContainer>
  );
};
export default AddNewPaymentMethod;
